import React, { useEffect, useState, useCallback } from "react";
import { Heart, X, Sparkles } from "lucide-react";
import { storage } from "../lib/storage";
import { trackPopupImpression, trackPopupAction } from "../lib/analytics";

const POPUP_ID = "feedback_uses_app";
const POPUP_VERSION = 1;
const ENGAGE_THRESHOLD = 3;
const SHOW_COOLDOWN_DAYS = 14;
const DISMISS_COOLDOWN_DAYS = 30;
const STATE_KEY = "gs_feedback_popup_state";

const now = () => Date.now();
const days = (n) => n * 24 * 60 * 60 * 1000;

function loadState() {
  const s = storage.get(STATE_KEY, {});
  return s && typeof s === "object" ? s : {};
}

function saveState(patch) {
  const s = { ...loadState(), ...patch, version: POPUP_VERSION };
  storage.set(STATE_KEY, s);
  return s;
}

function canShow() {
  const s = loadState();
  if (s.version !== POPUP_VERSION) return true;
  if (s.answered) return false;
  if (s.dismissedAt && now() - s.dismissedAt < days(DISMISS_COOLDOWN_DAYS)) return false;
  if (s.lastShownAt && now() - s.lastShownAt < days(SHOW_COOLDOWN_DAYS)) return false;
  return true;
}

const OPTIONS = [
  { key: "yes", label: "Yes, daily", emoji: "🙏", cls: "gold-gradient text-black shadow-lg shadow-amber-500/30" },
  { key: "sometimes", label: "Sometimes", emoji: "✨", cls: "bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20" },
  { key: "not_yet", label: "Not yet", emoji: "🌱", cls: "bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20" },
];

export default function FeedbackPopup() {
  const [open, setOpen] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const tryOpen = useCallback(() => {
    if (open) return;
    if (!canShow()) return;
    setOpen(true);
    saveState({ lastShownAt: now() });
    trackPopupImpression(POPUP_ID, { version: POPUP_VERSION });
  }, [open]);

  useEffect(() => {
    const hadEngaged = storage.get("gs_had_engaged_session", false);
    let openTimer = null;
    if (hadEngaged) {
      openTimer = setTimeout(tryOpen, 6000);
    }

    const onEngage = (e) => {
      const count = e?.detail?.count || 0;
      if (count >= ENGAGE_THRESHOLD) {
        setTimeout(tryOpen, 1500);
      }
    };
    window.addEventListener("gs:engagement", onEngage);
    return () => {
      window.removeEventListener("gs:engagement", onEngage);
      if (openTimer) clearTimeout(openTimer);
    };
  }, [tryOpen]);

  const answer = (key) => {
    saveState({ answered: true, answer: key, answeredAt: now() });
    trackPopupAction(POPUP_ID, key, { version: POPUP_VERSION });
    setAnswered(true);
    setTimeout(() => setOpen(false), 1400);
  };

  const later = () => {
    saveState({ dismissedAt: now() });
    trackPopupAction(POPUP_ID, "later", { version: POPUP_VERSION });
    setOpen(false);
  };

  if (!open || !mounted) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[59] bg-black/40 backdrop-blur-sm animate-fade-in"
        onClick={later}
      />
      
      {/* Popup Container - TOP POSITIONED */}
      <div className="fixed top-0 left-0 right-0 z-[60] flex justify-center px-3 sm:px-4 pt-12 sm:pt-16 pb-4 pointer-events-none">
        <div 
          className="w-full max-w-md pointer-events-auto animate-slide-down"
          style={{ 
            animation: "slideDown 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
          data-testid="feedback-popup"
        >
          <div className="mx-2 sm:mx-0 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-amber-400/30"
            style={{ 
              background: "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(7,16,26,0.98))",
              backdropFilter: "blur(20px)",
            }}>
            
            {/* Header with gradient */}
            <div className="relative h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500" />
            
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className="w-11 h-11 sm:w-12 sm:h-12 shrink-0 rounded-full gold-gradient grid place-items-center text-black shadow-lg shadow-amber-500/30 animate-pulse-glow">
                  <Heart size={20} className="sm:w-5 sm:h-5" />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0 pt-0.5">
                  {!answered ? (
                    <>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Sparkles size={14} className="text-amber-400" />
                        <p className="text-white font-bold text-base sm:text-lg leading-tight">
                          Is GurSetu part of your daily jeevan?
                        </p>
                      </div>
                      <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                        Your honest answer helps us serve the Sangat better.
                      </p>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 grid place-items-center">
                        <Heart size={16} className="text-emerald-400" />
                      </div>
                      <p className="text-white font-bold text-base sm:text-lg">
                        Thanks🙏for Sharing.
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Close button */}
                <button 
                  onClick={later} 
                  className="p-1.5 -mr-1 -mt-1 rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-all active:scale-90"
                  data-testid="feedback-dismiss"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Options */}
              {!answered && (
                <div className="mt-5 space-y-2.5">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {OPTIONS.map((o, idx) => (
                      <button
                        key={o.key}
                        onClick={() => answer(o.key)}
                        data-testid={`feedback-opt-${o.key}`}
                        className={`py-3 px-3 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95 hover:scale-[1.02] ${o.cls}`}
                        style={{
                          animation: `fadeInUp 0.4s ease ${idx * 0.1}s both`
                        }}
                      >
                        <span className="mr-1.5">{o.emoji}</span>
                        {o.label}
                      </button>
                    ))}
                  </div>
                  
                  <button 
                    onClick={later} 
                    className="w-full py-2.5 text-xs sm:text-sm text-slate-400 hover:text-slate-300 transition-colors font-medium"
                    data-testid="feedback-later"
                  >
                    Ask me later
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-100%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(245, 158, 11, 0.4);
          }
          50% {
            box-shadow: 0 0 30px rgba(245, 158, 11, 0.6);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}